<script setup lang="ts">
import type { SiteElementGridAffiliateFind } from '@hostinger/builder-schema-validator/schema/schemaTypes';
import {
	computed,
	reactive,
} from 'vue';

import { MOBILE_BLOCK_WIDTH } from '@zyro-inc/site-modules/components/blocks/layout/constants';
import GridButton from '@zyro-inc/site-modules/components/elements/button/GridButton.vue';
import GridTextBox from '@zyro-inc/site-modules/components/elements/text-box/GridTextBox.vue';
import { convertWeightStringToNumber } from '@zyro-inc/site-modules/utils/font';

type Props = {
	data: SiteElementGridAffiliateFind;
	isInBuilder?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isInBuilder: false,
});

const emit = defineEmits<{
	click: [event: MouseEvent];
	drag: [event: DragEvent];
	dragstart: [event: DragEvent];
}>();

const computedData = reactive({
	// Content
	title: computed(() => props.data.content || 'New Affiliate Find'),
	buttonText: computed(() => props.data.buttonText || 'Shop Now'),
	buttonHref: computed(() => props.data.href || null),
	buttonTarget: computed(() => props.data.target || null),
	buttonRel: computed(() => props.data.rel || null),

	// Card
	cardBackgroundColor: computed(() => props.data.cardBackgroundColor || 'rgb(255, 255, 255)'),
	cardBorderColor: computed(() => props.data.cardBorderColor || 'rgb(229, 231, 235)'),
	cardBorderRadius: computed(() => props.data.cardBorderRadius),
	cardBorderWidth: computed(() => props.data.cardBorderWidth || 0),
	cardPadding: computed(() => props.data.cardPadding || 24),

	// Title
	titleTextAlign: computed(() => props.data.settings?.styles?.text || 'center'),
	titleBackgroundColor: computed(() => props.data.titleBackgroundColor),
	titleFontFamily: computed(() => props.data.titleFontFamily),
	titleFontWeight: computed(() => props.data.titleFontWeight),
	titleColor: computed(() => props.data.titleColor),
	titleFontSize: computed(() => props.data.titleFontSize),

	// Button
	buttonBackgroundColor: computed(() => props.data.backgroundColor),
	buttonBackgroundColorHover: computed(() => props.data.backgroundColorHover),
	buttonFontColor: computed(() => props.data.fontColor),
	buttonFontColorHover: computed(() => props.data.fontColorHover),
	buttonBorderColor: computed(() => props.data.borderColor),
	buttonBorderColorHover: computed(() => props.data.borderColorHover),
	buttonDesktop: computed(() => props.data.desktop),
	buttonMobile: computed(() => props.data.mobile),
	buttonFontFamily: computed(() => props.data.fontFamily),
	buttonFontWeight: computed(() => props.data.fontWeight),
	buttonBorderWidth: computed(() => props.data.borderWidth),
	buttonBorderRadius: computed(() => props.data.borderRadius),

	// Layout
	mobileElementWidth: computed(() => props.data.settings?.styles?.['m-element-width'] || 0),
	mobileElementHeight: computed(() => props.data.settings?.styles?.['m-element-height'] || 0),
	buttonSelfAlign: computed(() => props.data.settings?.styles?.align || 'stretch'),
});

const buttonFontSizeDesktop = computed(() => computedData.buttonDesktop?.fontSize);
const buttonFontSizeMobile = computed(() => computedData.buttonMobile?.fontSize);
const mobileWidthCSSVar = computed(() => `${(computedData.mobileElementWidth * 100) / MOBILE_BLOCK_WIDTH}vw`);
const mobileHeightCSSVar = computed(() => `${(computedData.mobileElementHeight * 100) / MOBILE_BLOCK_WIDTH}vw`);
const buttonFontWeightNumber = computed(() => convertWeightStringToNumber(computedData.buttonFontWeight));

const cardCSSVars = computed(() => ({
	'--card-background-color': computedData.cardBackgroundColor,
	'--card-border-color': computedData.cardBorderColor,
	'--card-border-radius': `${computedData.cardBorderRadius}px`,
	'--card-border-width': `${computedData.cardBorderWidth}px`,
	'--card-padding': `${computedData.cardPadding}px`,
	'--m-height': props.isInBuilder ? '100%' : mobileHeightCSSVar.value,
	'--m-width': props.isInBuilder ? '100%' : mobileWidthCSSVar.value,
	'--title-text-align': computedData.titleTextAlign,
	'--button-align': computedData.buttonSelfAlign,
	'--title-font-family': computedData.titleFontFamily,
	'--title-font-weight': String(computedData.titleFontWeight),
	'--title-color': computedData.titleColor,
	'--title-font-size': `${computedData.titleFontSize}px`,
}));
</script>

<template>
	<div
		class="grid-affiliate-find"
		data-qa="grid-affiliate-find"
		:style="cardCSSVars"
		@click="emit('click', $event)"
		@drag="emit('drag', $event)"
		@dragstart="emit('dragstart', $event)"
	>
		<div class="grid-affiliate-find__content">
			<GridTextBox
				:text-align="computedData.titleTextAlign"
				:background-color="computedData.titleBackgroundColor"
				:content="computedData.title"
				class="grid-affiliate-find__title"
			/>
			<GridButton
				:content="computedData.buttonText"
				:href="computedData.buttonHref"
				:target="computedData.buttonTarget"
				:rel="computedData.buttonRel"
				:background-color="computedData.buttonBackgroundColor"
				:background-color-hover="computedData.buttonBackgroundColorHover"
				:font-color="computedData.buttonFontColor"
				:font-color-hover="computedData.buttonFontColorHover"
				:border-color="computedData.buttonBorderColor"
				:border-color-hover="computedData.buttonBorderColorHover"
				:font-size-desktop="buttonFontSizeDesktop"
				:font-size-mobile="buttonFontSizeMobile"
				:font-family="computedData.buttonFontFamily"
				:font-weight="buttonFontWeightNumber"
				:border-width="computedData.buttonBorderWidth"
				:border-radius="computedData.buttonBorderRadius"
				class="grid-affiliate-find__button"
			/>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";

.grid-affiliate-find {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	min-height: var(--m-height, auto);
	min-width: var(--m-width, auto);
	background-color: var(--card-background-color);
	border: var(--card-border-width) solid var(--card-border-color);
	border-radius: var(--card-border-radius);

	&__content {
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: 1fr;
		grid-gap: 16px;
		padding: var(--card-padding);
		width: 100%;
	}

	&__title {
		text-align: var(--title-text-align);
		font-family: var(--title-font-family, var(--font-primary));
		font-weight: var(--title-font-weight, var(--body-font-weight));
		color: var(--title-color, var(--body-color));
		font-size: var(--title-font-size, var(--body-font-size));
	}

	&__button {
		justify-self: var(--button-align);
		display: flex;
		justify-content: center;
		align-items: center;
	}
}

</style>
